import React, { Component, useRef, useState, useEffect } from 'react';
import {Link, useNavigate, useParams } from "react-router-dom";
import Bracket from './Bracket.js';
import NavBarMini from './NavBarMini.js';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';

import GameContainer from './GameContainer';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp, writeBatch } from "firebase/firestore";
import { db } from '../firebase.js';

const DATABASE_SOURCE = utils.getDatabaseSource();

let DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED = new Date('May 27, 2024');
let SAVE_BLOCKS_AND_STEALS = true;

const ProgressModal = ({ visible, titles, status, step, progress, error, doneMessage, onClose }) => {
  if (!visible) return null;

  return (
    <div className="article-modal-overlay">
      <div className="article-modal">
        <p className="article-modal-title">
          {status === 'done' ? titles.done : status === 'error' ? titles.error : titles.inProgress}
        </p>

        {status === 'inProgress' && (
          <>
            <div className="progress article-modal-progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-dark"
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
            <p className="article-modal-note">{step}</p>
          </>
        )}

        {status === 'done' && (
          <p className="article-modal-note">{doneMessage}</p>
        )}

        {status === 'error' && (
          <>
            <p className="article-modal-note">{error}</p>
            <button className="btn btn-dark article-modal-btn" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const AdminSeasonPlayoffs = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { isReady } = useAdminGuard();
  const SEASON = config.currentSeason;
  const { season } = useParams();
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([])
  const [teams, setTeams] = useState([])
  const [seasonTeams, setSeasonTeams] = useState([])
  const [allGameStats, setAllGameStats] = useState([])
  const [gameStats, setGameStats] = useState({})
  const [playoffGameViewToggle, setPlayoffGameViewToggle] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [currentSeason, setCurrentSeason] = useState({})
  const [playoffGames, setPlayoffGames] = useState({})
  const [seedInputs, setSeedInputs] = useState({
    east: Array(8).fill(''),
    west: Array(8).fill('')
  });
  const [gameTimes, setGameTimes] = useState({});
  const [playoffTagsDrafts, setPlayoffTagsDrafts] = useState({}); // gameID -> staged tags array, not yet saved
  const [newTagInputs, setNewTagInputs] = useState({}); // gameID -> text currently typed into the "add tag" input
  const [showCreatePlayoffsModal, setShowCreatePlayoffsModal] = useState(false);
  const [createPlayoffsStatus, setCreatePlayoffsStatus] = useState('idle'); // 'idle' | 'inProgress' | 'done' | 'error'
  const [showSaveGameTimesModal, setShowSaveGameTimesModal] = useState(false);
  const [saveGameTimesStatus, setSaveGameTimesStatus] = useState('idle'); // 'idle' | 'inProgress' | 'done' | 'error'
  const [saveGameTimesStep, setSaveGameTimesStep] = useState('');
  const [saveGameTimesProgress, setSaveGameTimesProgress] = useState(0);
  const [saveGameTimesError, setSaveGameTimesError] = useState('');
  const [createPlayoffsStep, setCreatePlayoffsStep] = useState('');
  const [createPlayoffsProgress, setCreatePlayoffsProgress] = useState(0);
  const [createPlayoffsError, setCreatePlayoffsError] = useState('');

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      await getCurrentSeasonData();
      await getLeague();
      setDataReady(true);
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentSeasonData = async () => {
    const seasonRef = doc(db, "seasons", season);
    const seasonSnapshot = await getDoc(seasonRef);
    const seasonData = seasonSnapshot.data();

    setCurrentSeason(seasonData);
  }

  const getLeague = async () => {
    const gameStatsCollectionRef = collection(db, "gameStats");
    const gameStatsSnapshot = await getDocs(gameStatsCollectionRef);
    const allGameStats = [];
    const gameStats = {};
    gameStatsSnapshot.forEach(doc => {
      const gameID = doc.id
      let stats = doc.data()['stats'] || [];
      stats = stats.map((stat) => {
        stat.gameID = gameID
        return stat
      })
      gameStats[gameID] = stats;
      allGameStats.push(...stats);
    });

    await setAllGameStats(allGameStats)
    await setGameStats(gameStats)

    const playerSnapshot = await getDocs(collection(db, 'players'));
    const players = playerSnapshot.docs.map((doc) => doc.data());

    setPlayers(players)

    const gamesRef = collection(db, "games");
    const gamesSnapshot = await getDocs(gamesRef);
    let gamesData = gamesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    let games = gamesData.map(game => ({
      ...game,
      date: game.date.toDate()
    }));
    games.sort((a, b) => a.date - b.date);
    games = games.filter(g => g.season == parseInt(season))
    setGames(games)

    let playoffGames = games.filter((g) => g.type == 'playoff')
    setPlayoffGames(playoffGames)

    const teamsRef = collection(db, "teams");
    const teamsSnapshot = await getDocs(teamsRef);
    const teamsData = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    setTeams(teamsData)

    const seasonTeamsRef = collection(db, "seasonTeams");
    const seasonTeamsSnapshot = await getDocs(seasonTeamsRef);
    const seasonTeamsData = seasonTeamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    let thisSeasonTeamData = seasonTeamsData.filter(st => st.season == parseInt(season));

    const teams = thisSeasonTeamData.map(st => { {
      const team = teamsData.find(t => t.id === st.teamID);
      return {
        ...st,
        ...team
      };
    }})

    setSeasonTeams(teams);

    if (playoffGames.length == 0 || playoffGames.every(game => game.awayTeamID == '')) {
      let seededSeasonTeams = utils.getSeasonTeams(games, teams, String(season))
      let seededConferenceTeams = {
        east: seededSeasonTeams.filter(t => t.conference == 'east').sort((a, b) => parseInt(a.seed) - parseInt(b.seed)).map(t => t.id),
        west: seededSeasonTeams.filter(t => t.conference == 'west').sort((a, b) => parseInt(a.seed) - parseInt(b.seed)).map(t => t.id)
      }

      setSeedInputs(seededConferenceTeams)
    }

    setDataReady(true);
  }

  useEffect(() => {
    if (playoffGames.length > 0) {
      const firstGames = playoffGames.filter(
        (g) => g.playoffTags.includes('first-round') || g.playoffTags.includes('play-in')
      );

      const eastCount = seasonTeams.filter(t => t.conference === 'east').length;
      const westCount = seasonTeams.filter(t => t.conference === 'west').length;

      const newSeedInputs = { east: Array(eastCount).fill(''), west: Array(westCount).fill('') };

      for (let i = 0; i < firstGames.length; i++) {
        const game = firstGames[i];
        const conf = game.playoffTags.includes('east') ? 'east' : 'west';

        const matchup = game.playoffTags.find(t => /\dvs\d/.test(t));

        if (!matchup) continue;

        const [seed1, seed2] = matchup.split('vs')

        if (seed1 && !seed1.includes('/') && game.awayTeamID) {
          let seed1Num = parseInt(seed1);
          newSeedInputs[conf][seed1Num - 1] = game.awayTeamID;
        }
        if (seed2 && !seed2.includes('/') && game.homeTeamID) {
          let seed2Num = parseInt(seed2);
          newSeedInputs[conf][seed2Num - 1] = game.homeTeamID;
        }
      }

      setSeedInputs(newSeedInputs);
    }
  }, [playoffGames]);

  function handleSeedInputChange(conf, index, value) {
    const newInputs = { ...seedInputs };
    newInputs[conf][index] = value;
    setSeedInputs(newInputs);
  }

  async function saveSeeds() {
    const firstGames = games.filter(
      (g) => g.playoffTags && (g.playoffTags.includes('first-round') || g.playoffTags.includes('play-in'))
    );

    const updatedGames = [...games];
    const updatedSeasonTeams = [...seasonTeams];

    for (const game of firstGames) {
      const conf = game.playoffTags.includes('east') ? 'east' : 'west';
      const matchup = game.playoffTags.find(t => /\dvs\d/.test(t));
      if (!matchup) continue;

      const [seed1, seed2] = matchup.split('vs');

      if (!seed1.includes('/')){
        const seed1Num = parseInt(seed1)
        const awayID = seedInputs[conf][seed1Num - 1];
        const awayTeam = teams.find(t => t.id === awayID);
        const awaySeasonTeam = updatedSeasonTeams.find(t => t.id === awayID && t.season == season);

        if (awayTeam) {
          game.awayTeamID = awayTeam.id;
          game.awayTeamName = awayTeam.name;
          if (awaySeasonTeam) {
            awaySeasonTeam.seed = seed1
          }
        }
      }
      
      if (!seed2.includes('/')) {
        const seed2Num = parseInt(seed2)
        const homeID = seedInputs[conf][seed2Num - 1];
        const homeTeam = teams.find(t => t.id === homeID);
        const homeSeasonTeam = updatedSeasonTeams.find(t => t.id === homeID && t.season == season);

        if (homeTeam) {
          game.homeTeamID = homeTeam.id;
          game.homeTeamName = homeTeam.name;
          if (homeSeasonTeam) {
            homeSeasonTeam.seed = seed2
          }
        }
      }

      const index = updatedGames.findIndex(g => g.id === game.id);
      updatedGames[index] = game;

      const gameRef = doc(db, "games", game.id);
      
      const gameUpdate = {};
      if (game.homeTeamID !== undefined) gameUpdate.homeTeamID = game.homeTeamID;
      if (game.homeTeamName !== undefined) gameUpdate.homeTeamName = game.homeTeamName;
      if (game.awayTeamID !== undefined) gameUpdate.awayTeamID = game.awayTeamID;
      if (game.awayTeamName !== undefined) gameUpdate.awayTeamName = game.awayTeamName;

      await updateDoc(gameRef, gameUpdate);
    }

    await setGames(updatedGames);
    await setSeasonTeams(updatedSeasonTeams);

    // window.location.reload();
  }

  // Only disable once there's nothing left to legitimately do with this
  // button: the bracket has started (some game has a recorded winner) AND
  // every first-round/play-in game already has both teams seeded in. If
  // playoffs haven't started yet, or a conference still needs seeding
  // (e.g. one conference started while the other hasn't), stay enabled.
  function isSaveSeedsDisabled () {
    const playoffsStarted = playoffGames.some(game => game.winner && game.winner !== '');
    const seedGames = playoffGames.filter(g => g.playoffTags && (g.playoffTags.includes('first-round') || g.playoffTags.includes('play-in')));
    const allSeedsSet = seedGames.length > 0 && seedGames.every(g => g.homeTeamID && g.awayTeamID);

    return playoffsStarted && allSeedsSet;
  }

  const savePlayoffGames = async (plyfGames) => {
    const batch = writeBatch(db);
    const gamesCollection = collection(db, "games");

    plyfGames.forEach((game) => {
      const gameRef = doc(gamesCollection, game.id);
      batch.set(gameRef, game);
    });

    await batch.commit();
  };

  const saveSeasonTeams = async (updatedSeasonTeams)=> {
    try {
      const seasonId = String(season);
      const seasonRef = doc(db, "seasons", seasonId);

      const sznTeams = utils.getSeasonTeams(games, seasonTeams, seasonId);

      // await updateDoc(seasonRef, {
      //   teams: sznTeams,
      // });
    } catch (error) {
      console.error('Error saving season teams:', error);
    }
  }

  async function savePlayoffGameTimes() {
    setShowSaveGameTimesModal(true);
    setSaveGameTimesStatus('inProgress');
    setSaveGameTimesError('');
    setSaveGameTimesStep('Saving game times…');
    setSaveGameTimesProgress(20);

    try {
      // 1. Get the playoff games for this season
      const plyfGms = games.filter((g) => g.type === 'playoff' && g.season == season);

      // 2. Keep track of promises for batch updating
      const updatePromises = [];

      for (const game of plyfGms) {
        if (game.winner) continue; // Don't touch games that have already finished

        const newDateStr = gameTimes[game.id];
        if (!newDateStr) continue; // Skip if no new date

        const newDate = new Date(newDateStr);
        if (isNaN(newDate.getTime())) {
          console.warn(`Invalid date for game ${game.id}:`, newDateStr);
          continue;
        }

        // Update the game object in Firestore
        const gameRef = doc(db, "games", game.id);
        const promise = updateDoc(gameRef, {
          date: newDate
        });

        updatePromises.push(promise);
      }

      setSaveGameTimesProgress(60);

      // 3. Wait for all updates to complete
      await Promise.all(updatePromises);

      setSaveGameTimesStep('Refreshing data…');
      setSaveGameTimesProgress(85);
      await getLeague();

      setSaveGameTimesStep('Done!');
      setSaveGameTimesProgress(100);
      setSaveGameTimesStatus('done');
      setTimeout(() => setShowSaveGameTimesModal(false), 600);
    } catch (error) {
      console.error('Error updating playoff games:', error);
      setSaveGameTimesError(error.message || 'Something went wrong.');
      setSaveGameTimesStatus('error');
    }
  }

  function toggleToShowPlayoffGames () {
    setPlayoffGameViewToggle(true);
  }

  function toggleToShowPlayoffBracket () {
    setPlayoffGameViewToggle(false);
  }


  // START : Eventually move these out...
  //
  //
  // Two label sets share these 5 bracket positions: seasons with real
  // conferences use the hyphenated tags, seasons without conferences (1 & 2,
  // before conferences existed) use the plain tags they were originally
  // saved with — see the matching comment in Bracket.js's playoffRoundsOrder.
  // Games whose tags match neither set show up in "Other / Uncategorized"
  // below so they can be found and retagged via the editor in displayGame().
  const KNOWN_ROUND_TAGS = [
    'play-in',
    'first-round', 'firstround',
    'conference-semifinal', 'quarterfinals',
    'conference-final', 'semifinals',
    'final', 'championship'
  ];

  // The working (unsaved) tag list for a game — falls back to its actual
  // saved tags until the admin removes/adds something, so edits are staged
  // locally and only committed to Firestore when "Save Tags" is clicked.
  function getTagsDraft (game) {
    return playoffTagsDrafts[game.id] !== undefined ? playoffTagsDrafts[game.id] : (game.playoffTags || []);
  }

  function removeTagFromDraft (game, tagToRemove) {
    const updated = getTagsDraft(game).filter(t => t !== tagToRemove);
    setPlayoffTagsDrafts(prev => ({ ...prev, [game.id]: updated }));
  }

  function addTagToDraft (game) {
    const newTag = (newTagInputs[game.id] || '').trim();
    if (!newTag) return;

    const current = getTagsDraft(game);
    if (!current.includes(newTag)) {
      setPlayoffTagsDrafts(prev => ({ ...prev, [game.id]: [...current, newTag] }));
    }
    setNewTagInputs(prev => ({ ...prev, [game.id]: '' }));
  }

  async function saveGameTags (game) {
    const newTags = getTagsDraft(game);

    await updateDoc(doc(db, 'games', game.id), { playoffTags: newTags });

    const updatedGames = games.map(g => g.id === game.id ? { ...g, playoffTags: newTags } : g);
    setGames(updatedGames);
    setPlayoffGames(updatedGames.filter((g) => g.type == 'playoff'));

    setPlayoffTagsDrafts(prev => {
      const updated = { ...prev };
      delete updated[game.id];
      return updated;
    });
  }

  function displayGames () {
    const playinGames = playoffGames.filter((game) => game.playoffTags.includes('play-in'))
    const firstRoundGames = playoffGames.filter((game) => game.playoffTags.includes('first-round') || game.playoffTags.includes('firstround'))
    const confSemiGames = playoffGames.filter((game) => game.playoffTags.includes('conference-semifinal') || game.playoffTags.includes('quarterfinals'))
    const confFinalGames = playoffGames.filter((game) => game.playoffTags.includes('conference-final') || game.playoffTags.includes('semifinals'))
    const championshipGame = playoffGames.filter((game) => game.playoffTags.includes('final') || game.playoffTags.includes('championship'))
    const uncategorizedGames = playoffGames.filter((game) => !game.playoffTags.some(t => KNOWN_ROUND_TAGS.includes(t)))

    let playinGameElements = playinGames.map((game, place) => displayGame(game) )
    let firstRoundGameElements = firstRoundGames.map((game, place) => displayGame(game) )
    let confSemiGameElements = confSemiGames.map((game, place) => displayGame(game) )
    let confFinalGameElements = confFinalGames.map((game, place) => displayGame(game) )
    let championshipGameElements = championshipGame.map((game, place) => displayGame(game) )
    let uncategorizedGameElements = uncategorizedGames.map((game, place) => displayGame(game) )

    return (
      <>
        { uncategorizedGames.length > 0 ?
        <div className="league-table-container schedule">
          <p>Other / Uncategorized (edit tags below to assign a round)</p>
          { uncategorizedGameElements }
        </div>
        : '' }

        { playinGames.length > 0 ?
        <div className="league-table-container schedule">
          <p>Play-In</p>
          { playinGameElements }
        </div>
        : '' }

        <div className="league-table-container schedule">
          <p>First Round</p>
          { firstRoundGameElements }
        </div>

        <div className="league-table-container schedule">
          <p>Conference Semifinals</p>
          { confSemiGameElements }
        </div>

        <div className="league-table-container schedule">
          <p>Conference Finals</p>
          { confFinalGameElements }
        </div>

        <div className="league-table-container schedule">
          <p>Finals</p>
          { championshipGameElements }
        </div>
      </>

    );
  }

  function displayGame (game) {
    const showPlaceholders = false;
    const showSeeds = true;
    const tagsDraft = getTagsDraft(game);

    return (
      <div className='game-edit-block'>
        <p className="game-id-label">{game.id}</p>

        <div className='game-container-with-input'>
          <GameContainer games={games} teams={seasonTeams} gameID={game.id} showPlaceholders={showPlaceholders} showSeeds={showSeeds}></GameContainer>
          <input
            type="datetime-local"
            value={gameTimes[game.id] || utils.formatDateInputValue(game.date)}
            onChange={(e) =>
              setGameTimes(prev => ({ ...prev, [game.id]: e.target.value }))
            }
          />
        </div>

        <div className='playoff-tags-editor'>
          <div className='playoff-tags-chip-list'>
            {tagsDraft.map((tag) => (
              <span className='playoff-tag-chip' key={tag}>
                {tag}
                <button type="button" className='playoff-tag-chip-remove' onClick={() => removeTagFromDraft(game, tag)} aria-label={`Remove tag ${tag}`}>×</button>
              </span>
            ))}
          </div>
          <div className='playoff-tags-add-row'>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Add tag"
              value={newTagInputs[game.id] || ''}
              onChange={(e) => setNewTagInputs(prev => ({ ...prev, [game.id]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTagToDraft(game); } }}
            />
            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addTagToDraft(game)}>+</button>
            <button className="btn btn-sm btn-dark" type="button" onClick={() => saveGameTags(game)}>Save Tags</button>
          </div>
        </div>
      </div>
    )
  }

  // END : Eventually move these out...

  function getNextWeekdays(startDate, count) {
    const results = [];
    const d = new Date(startDate);

    while (results.length < count) {

      const day = d.getDay(); // 0=Sun, 6=Sat
      if (day !== 0 && day !== 6) {
        const dateWithTime = new Date(d);
        dateWithTime.setHours(6, 30, 0, 0); // 6:30am
        results.push(dateWithTime);
      }

      d.setDate(d.getDate() + 1); // move forward one day
    }

    return results;
  }

  function formatPlayoffDate(d) {
    const month = d.toLocaleString("en-US", { month: "long" });
    const day = d.getDate();
    const year = d.getFullYear();

    const hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${month} ${day}, ${year} ${hours}:${minutes}`;
  }

  async function createPlayoffs() {
    setShowCreatePlayoffsModal(true);
    setCreatePlayoffsStatus('inProgress');
    setCreatePlayoffsError('');
    setCreatePlayoffsStep('Building bracket…');
    setCreatePlayoffsProgress(15);

    try {
      await buildAndSavePlayoffs();

      setCreatePlayoffsStep('Done!');
      setCreatePlayoffsProgress(100);
      setCreatePlayoffsStatus('done');
      setTimeout(() => setShowCreatePlayoffsModal(false), 600);
    } catch (error) {
      console.error('Error creating playoffs:', error);
      setCreatePlayoffsError(error.message || 'Something went wrong.');
      setCreatePlayoffsStatus('error');
    }
  }

  async function buildAndSavePlayoffs() {
    // Start the playoff schedule on the Monday of the week following the
    // season's last regular season game, rather than a hardcoded date that
    // quickly goes stale.
    const regularSeasonGames = games.filter(g => g.type !== 'playoff');
    const lastRegularSeasonGameDate = regularSeasonGames.length > 0
      ? new Date(Math.max(...regularSeasonGames.map(g => g.date)))
      : new Date();

    const daysSinceMonday = (lastRegularSeasonGameDate.getDay() + 6) % 7;
    const mondayOfLastGameWeek = new Date(lastRegularSeasonGameDate);
    mondayOfLastGameWeek.setDate(lastRegularSeasonGameDate.getDate() - daysSinceMonday);

    let startDate = new Date(mondayOfLastGameWeek);
    startDate.setDate(mondayOfLastGameWeek.getDate() + 7);

    // Conferences are not guaranteed to be the same size (e.g. an odd total
    // team count), so play-in counts must be computed per conference rather
    // than assuming an even split.
    const conferenceTeamCounts = {
      east: seasonTeams.filter(t => t.conference == 'east').length,
      west: seasonTeams.filter(t => t.conference == 'west').length
    };
    const playInCounts = {
      east: Math.max(0, conferenceTeamCounts.east - 8),
      west: Math.max(0, conferenceTeamCounts.west - 8)
    };
    const maxPlayInGames = Math.max(playInCounts.east, playInCounts.west);

    // 1 game day per play-in round + 4 first-round days + 2 semifinal days + 1 conference-final day + 1 final day
    let numberOfGameDays = maxPlayInGames + 8;
    const playoffDates = getNextWeekdays(startDate, numberOfGameDays).map(formatPlayoffDate);

    const defaultPlayoffGame = {
      awayTeamID: '',
      awayTeamPlaceholder: '',
      date: '',
      homeTeamID: '',
      homeTeamPlaceholder: '',
      id: '',
      location: '',
      playoffRoundOrder: 0,
      playoffTags: [],
      referees: [],
      score: {
        away: 0,
        home: 0
      },
      season: parseInt(season),
      status: 'scheduled',
      type: 'playoff',
      winner: ''
    };

    let plyfGames = [];

    const placeMap = {
      1: '1st',
      2: '2nd',
      3: '3rd',
      4: '4th',
      5: '5th',
      6: '6th',
      7: '7th',
      8: '8th',
      9: '9th',
      10: '10th',
      11: '11th',
      12: '12th'
    };

    const conferences = ['east', 'west'];
    const locations = {
      east: 'sbca',
      west: 'b&gc'
    };

    // Matchups by seed for first round
    const firstRoundMatchups = utils.PLAYOFF_FIRST_ROUND_MATCHUPS;

    const gameRefs = {
      east: {
        'play-in': [],
        'first-round': [],
        'conference-semifinal': [],
        'conference-final': []
      },
      west: {
        'play-in': [],
        'first-round': [],
        'conference-semifinal': [],
        'conference-final': []
      }
    };

    let playoffGameIndex = 0

    setCreatePlayoffsStep('Building play-in games…');
    setCreatePlayoffsProgress(30);

    // === PLAY-IN GAMES ===
    // - 8 will work for between 16 and 32 teams since
    // 16 teams => 16 / 2 = 8, 8 - 8 = 0 play-in games
    // 32 teams => 32 / 2 = 16, 16 - 8 = 8 play-in games (everyone plays-in)
    let round = 'play-in'
    let prevRound = ''
    let playoffRoundOrder = 1;
    // Tracked per conference: each conference may need a different number of
    // play-in games (or none), so a conference only gets a game on a given
    // play-in day if it actually has that many teams over 8.
    const playinWinnerSeeds = { east: [], west: [] };
    for (let i = 0; i < maxPlayInGames; i++) {
      const seedWorse = 9 + i
      const seedBetter = 8 - i;

      const gameDate = Timestamp.fromDate(new Date(playoffDates[playoffGameIndex++]));

      let matchupString = `${seedBetter}vs${seedWorse}`;
      let winnerSeeds = [seedBetter, seedWorse]

      for (const conference of conferences) {
        if (i >= playInCounts[conference]) continue;

        playinWinnerSeeds[conference].push(winnerSeeds)

        const game = { ...defaultPlayoffGame };
        game.id = utils.createNewIDForGame();
        game.date = gameDate;
        game.location = locations[conference];
        game.playoffRoundOrder = playoffRoundOrder++;
        game.playoffTags = [
          round,
          matchupString,
          conference
        ];
        game.awayTeamPlaceholder = `${placeMap[seedBetter]} Seed`;
        game.homeTeamPlaceholder = `${placeMap[seedWorse]} Seed`;
        plyfGames.push(game);
        gameRefs[conference][round].push(game);
      }
    }

    setCreatePlayoffsStep('Building first round…');
    setCreatePlayoffsProgress(45);

    // === FIRST ROUND ===
    playoffRoundOrder = 1;
    round = 'first-round'
    prevRound = 'play-in'
    for (let i = 0; i < firstRoundMatchups.length; i++) {
      const seeds = firstRoundMatchups[i];
      const gameDate = Timestamp.fromDate(new Date(playoffDates[playoffGameIndex++]));

      for (const conference of conferences) {
        let [seed1, seed2] = seeds;
        const confPlayinWinners = playinWinnerSeeds[conference];

        if (confPlayinWinners.length > 8 - seed2) {
          seed2 = confPlayinWinners[8 - seed2];
        }

        let seed2placeholder = '';
        let seed2tag = ''
        if (Array.isArray(seed2)) {
          seed2placeholder = `${placeMap[seed2[0]]}/${placeMap[seed2[1]]} Seed`;
          seed2tag = `${seed2[0]}/${seed2[1]}`
        } else {
          seed2placeholder = `${placeMap[seed2]} Seed`;
          seed2tag = seed2
        }

        const game = { ...defaultPlayoffGame };
        game.id = utils.createNewIDForGame();
        game.date = gameDate;
        game.location = locations[conference];
        game.playoffRoundOrder = playoffRoundOrder++;
        game.playoffTags = [
          round,
          `${seed1}vs${seed2tag}`,
          conference
        ];
        game.awayTeamPlaceholder = `${placeMap[seed1]} Seed`;
        game.homeTeamPlaceholder = seed2placeholder;

        plyfGames.push(game);
        gameRefs[conference][round].push(game);
      }
    }

    setCreatePlayoffsStep('Building semifinals and finals…');
    setCreatePlayoffsProgress(60);

    // === SEMIFINALS ===
    // 2 semifinal games per conference
    playoffRoundOrder = 1
    round = 'conference-semifinal'
    prevRound = 'first-round'
    for (let i = 0; i < 2; i++) {
      const gameDate = Timestamp.fromDate(new Date(playoffDates[playoffGameIndex++]));

      for (const conference of conferences) {
        const prevGame1 = gameRefs[conference][prevRound][i * 2];
        const prevGame2 = gameRefs[conference][prevRound][i * 2 + 1];

        const game = { ...defaultPlayoffGame };
        game.id = utils.createNewIDForGame();
        game.date = gameDate;
        game.location = locations[conference];
        game.playoffRoundOrder = playoffRoundOrder++;
        game.playoffTags = [
          round,
          conference,
          'winner_' + prevGame1.id,
          'winner_' + prevGame2.id
        ];
        let [seedA1, seedA2] = firstRoundMatchups[i * 2];
        let [seedB1, seedB2] = firstRoundMatchups[i * 2 + 1];
        const confPlayinWinners = playinWinnerSeeds[conference];

        if (confPlayinWinners.length > 8 - seedA2) {
          game.awayTeamPlaceholder = `Winner of ${seedA1}vPlay-In`;
        } else {
          game.awayTeamPlaceholder = `Winner of ${seedA1}v${seedA2}`;
        }
        if (confPlayinWinners.length > 8 - seedB2) {
          game.homeTeamPlaceholder = `Winner of ${seedB1}vPlay-In`;
        } else {
          game.homeTeamPlaceholder = `Winner of ${seedB1}v${seedB2}`;
        }

        plyfGames.push(game);
        gameRefs[conference][round].push(game);
      }
    }

    // === CONFERENCE FINALS ===
    const confFinalDate = Timestamp.fromDate(new Date(playoffDates[playoffGameIndex++]));
    round = 'conference-final'
    prevRound = 'conference-semifinal'
    playoffRoundOrder = 1
    for (const conference of conferences) {
      const [semi1, semi2] = gameRefs[conference][prevRound];

      const game = { ...defaultPlayoffGame };
      game.id = utils.createNewIDForGame();
      game.date = confFinalDate;
      game.location = locations[conference];
      game.playoffRoundOrder = playoffRoundOrder++;
      game.playoffTags = [
        round,
        conference,
        'winner_' + semi1.id,
        'winner_' + semi2.id
      ];
      game.awayTeamPlaceholder = 'Winner of Semi 1';
      game.homeTeamPlaceholder = 'Winner of Semi 2';

      plyfGames.push(game);
      gameRefs[conference][round].push(game);
    }

    // === FINAL ===
    round = 'final'
    prevRound = 'conference-final'
    const finalDate = Timestamp.fromDate(new Date(playoffDates[playoffGameIndex++]));
    const finalGame = { ...defaultPlayoffGame };

    const eastFinal = gameRefs.east[prevRound][0];
    const westFinal = gameRefs.west[prevRound][0];

    finalGame.id = utils.createNewIDForGame();
    finalGame.date = finalDate;
    finalGame.location = 'sbca';
    finalGame.playoffRoundOrder = 1;
    finalGame.playoffTags = [
      round,
      'winner_' + eastFinal.id,
      'winner_' + westFinal.id
    ];
    finalGame.awayTeamPlaceholder = 'Eastern Champion';
    finalGame.homeTeamPlaceholder = 'Western Champion';

    plyfGames.push(finalGame);

    setCreatePlayoffsStep('Saving playoff games…');
    setCreatePlayoffsProgress(75);
    await savePlayoffGames(plyfGames);

    setCreatePlayoffsStep('Refreshing data…');
    setCreatePlayoffsProgress(90);
    await getLeague();
  }

  function displayPlayoffGames () {
    return (
      <div className="league-table-container schedule">
        { displayGames() }
      </div>
    )
  }

  function displayPlayoffBracket () {
    return (
      <>
        <Bracket games={games} teams={seasonTeams} isCurrentSeason={ SEASON == season } showPlaceholders={ false } compact></Bracket>
      </>
    )
  }

  if (dataReady == false) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Schedule">
        <div className="league-content">
          <div className="league-body admin-season-playoffs-body">
            <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
            <p className='league-admin-back-button'><a className='league-link' href={"/admin/seasons/" + season}><b>← Back to Season { season }</b></a></p>

            <p className="league-title-standard-secondary text-align-center">Edit Playoffs</p>
            <div className="btn-group league-radio-btn-group" role="group" aria-label="League/conference radio toggle button group">
              <input type="radio" className="btn-check" name="btnradio" id="btnLeague" autoComplete="off" defaultChecked onClick={ () => { toggleToShowPlayoffGames() }}></input>
              <label className="btn league-btn-outline-primary" htmlFor="btnLeague">Game List</label>

              <input type="radio" className="btn-check" name="btnradio" id="btnConference" autoComplete="off" onClick={ () => { toggleToShowPlayoffBracket() }}></input>
              <label className="btn league-btn-outline-primary" htmlFor="btnConference">Bracket</label>
            </div>

            <p className="league-title-standard-secondary text-align-center">Seeding</p>
            <div className="seed-entry-table">
              <table className="table table-bordered table-sm text-center">
                <thead>
                  <tr>
                    <th>Seed</th>
                    <th>East Team ID</th>
                    <th>West Team ID</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.max(seedInputs.east.length, seedInputs.west.length) }, (_, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>
                        {i < seedInputs.east.length ?
                          <input
                            className="form-control form-control-sm"
                            value={seedInputs.east[i]}
                            onChange={(e) => handleSeedInputChange('east', i, e.target.value)}
                          /> : ''}
                      </td>
                      <td>
                        {i < seedInputs.west.length ?
                          <input
                            className="form-control form-control-sm"
                            value={seedInputs.west[i]}
                            onChange={(e) => handleSeedInputChange('west', i, e.target.value)}
                          /> : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-dark" type="button" onClick={ saveSeeds } disabled={ isSaveSeedsDisabled() }>Save Seeds</button>
            </div>
            <button className="btn btn-dark" type="button" onClick={ savePlayoffGameTimes } disabled={ showSaveGameTimesModal || playoffGames.every(game => game.winner) }>Save Game Times</button>


            { playoffGames.length == 0 ?
                <>
                  <br/>
                  <button className="btn btn-dark" type="button" onClick={ createPlayoffs } disabled={ showCreatePlayoffsModal }>+ Create Playoffs</button>
                  <p>There are no playoff games for season { season }.</p>
                </>
                : playoffGameViewToggle ?
                displayPlayoffGames() :
                displayPlayoffBracket()
            }
          </div>
        </div>

        <Footer/>

        <ProgressModal
          visible={showCreatePlayoffsModal}
          titles={{ inProgress: 'Creating Playoffs…', done: 'Playoffs Created', error: 'Failed to Create Playoffs' }}
          status={createPlayoffsStatus}
          step={createPlayoffsStep}
          progress={createPlayoffsProgress}
          error={createPlayoffsError}
          doneMessage="The playoff bracket has been created."
          onClose={() => setShowCreatePlayoffsModal(false)}
        />

        <ProgressModal
          visible={showSaveGameTimesModal}
          titles={{ inProgress: 'Saving Game Times…', done: 'Game Times Saved', error: 'Failed to Save Game Times' }}
          status={saveGameTimesStatus}
          step={saveGameTimesStep}
          progress={saveGameTimesProgress}
          error={saveGameTimesError}
          doneMessage="The playoff game times have been updated."
          onClose={() => setShowSaveGameTimesModal(false)}
        />
      </div>
    );
  }
}

export default AdminSeasonPlayoffs;
